import { useEffect } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { Button, NavTabBar } from '@asad/crane-ui'
import type { NavTabItem } from '@asad/crane-ui'

import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks'
import { fetchServiceRequests, refreshServiceRequests } from '@/store/slices/service-requests-slice'

const REQUESTS_REFRESH_MS = 60_000

const ServiceLayout = () => {
  const { pathname } = useLocation()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const requestsStatus = useAppSelector((state) => state.serviceRequests.status)
  const unseenRequestsCount = useAppSelector(
    (state) => state.serviceRequests.requests.filter((request) => !request.seenAt).length,
  )

  useEffect(() => {
    if (requestsStatus === 'idle') dispatch(fetchServiceRequests())
  }, [requestsStatus, dispatch])

  // Zgłoszenia klientów przychodzą bez powiadomienia z serwera — licznik zakładki odświeża się sam.
  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') dispatch(refreshServiceRequests())
    }
    const timer = window.setInterval(refreshWhenVisible, REQUESTS_REFRESH_MS)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [dispatch])

  const isOrdersTab = pathname === '/service' || pathname.startsWith('/service/orders')

  const tabs: NavTabItem[] = [
    {
      to: '/service',
      label: t('service.tabOrders'),
      match: (p) => p === '/service' || p.startsWith('/service/orders'),
    },
    {
      to: '/service/requests',
      label: t('service.tabRequests'),
      count: unseenRequestsCount > 0 ? unseenRequestsCount : undefined,
    },
    { to: '/service/archive', label: t('service.tabArchive') },
    { to: '/service/activities', label: t('service.tabActivities') },
  ]

  return (
    <div className='flex flex-col gap-3'>
      <NavTabBar
        items={tabs}
        pathname={pathname}
        actions={
          isOrdersTab ? (
            <Button size='sm' onClick={() => navigate('/service/orders/new')}>
              {t('service.createOrder')}
            </Button>
          ) : undefined
        }
      />
      <Outlet />
    </div>
  )
}

export default ServiceLayout
